import { create } from 'zustand';
import { Job, JobStatus } from '../generated/gql-types';
import { workerJobActions } from './workerJob.action';
import dayjs from 'dayjs';
import { createJSONStorage, persist } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { upsertMany } from '../util/zustand.util';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

const filterJobRecords = (jobs: Job[]): Job[] =>
  jobs.filter(
    (job): job is Job =>
      // Filter out non-Job objects (like GraphQL responses)
      job &&
      typeof job === 'object' &&
      !('data' in job) &&
      job.__typename === 'Job',
  );

interface WorkerJobStore {
  isLoading: boolean;
  errorCode: string;
  errorMessage: string;

  // Single source of truth for all jobs
  workerJobs: Job[];

  // Filters
  filterDistance: number | null;
  // Format: YYYY-MM-DD
  filterStartDateFrom: string | null;
  // Format: YYYY-MM-DD
  filterStartDateTo: string | null;

  // Cache invalidation flag
  shouldRefreshOpenJobs: boolean;

  // Helper functions for state management
  upsertJobs: (jobs: Job[]) => void;
  clearCachedJobsByStatus: (status: JobStatus[]) => void;

  // Setters
  setFilterDistance: (distance: number | null) => void;
  setFilterStartDateFrom: (startDate: string | null) => void;
  setFilterStartDateTo: (startDate: string | null) => void;
  setShouldRefreshOpenJobs: (shouldRefresh: boolean) => void;

  // API actions
  fetchPotentialJobs: () => Promise<Job[]>;
  fetchJobsByStatus: (
    status: JobStatus[],
    limit?: number,
    lastEvaluatedKey?: string,
  ) => Promise<{
    jobs: Job[];
    hasMore: boolean;
    lastEvaluatedKey?: string;
  }>;
  acceptJob: (id: string) => Promise<Job | null>;
  applyForJob: (id: string, coverLetter?: string) => Promise<any>;
  withdrawFromJob: (id: string, reason: string) => Promise<Job | null>;
  fetchJobById: (id: string) => Promise<Job | null>;

  // Getters
  getOpenJobs: () => Job[];
  getUpcomingJobs: () => Job[];
  getInProgressJobs: () => Job[];
  getCompletedJobs: () => Job[];
  getJobById: (id: string) => Job | undefined;
  getAppliedJobs: () => Job[];
}

export const useWorkerJobStore = create<WorkerJobStore>()(
  persist(
    (set, get) => ({
      // States
      isLoading: false,
      errorCode: '',
      errorMessage: '',

      // Single source of truth for all jobs
      workerJobs: [],

      // Filters
      filterDistance: null,
      filterStartDateFrom: null,
      filterStartDateTo: null,

      // Cache invalidation flag
      shouldRefreshOpenJobs: false,

      // Helper functions
      upsertJobs: (newJobs: Job[]) =>
        set((state) => ({
          // Keyed by id so a later page cannot insert a job already in the cache,
          // including duplicates already stored from earlier fetches.
          workerJobs: upsertMany(state.workerJobs, filterJobRecords(newJobs)),
        })),

      clearCachedJobsByStatus: (status: JobStatus[]) =>
        set((state) => ({
          workerJobs: state.workerJobs.filter(
            (job) => job.itemStatus && !status.includes(job.itemStatus),
          ),
        })),

      // Setters
      setFilterDistance: (distance: number | null) =>
        set({ filterDistance: distance }),

      setFilterStartDateFrom: (startDate: string | null) =>
        set({ filterStartDateFrom: startDate }),
      setFilterStartDateTo: (startDate: string | null) =>
        set({ filterStartDateTo: startDate }),
      setShouldRefreshOpenJobs: (shouldRefresh: boolean) =>
        set({ shouldRefreshOpenJobs: shouldRefresh }),
      // Actions
      fetchPotentialJobs: async () => {
        const jobs = await workerJobActions.getAllPotentialJobsForUserId();

        // Clear out unfilled jobs so the open jobs tab only shows valid potential
        // jobs returned by the API. Otherwise, unfilled jobs will always remain in
        // the store even if they are no longer valid/unfilled.
        get().clearCachedJobsByStatus([JobStatus.Unfilled]);

        if (jobs.length > 0) {
          get().upsertJobs(jobs);
        }
        return jobs;
      },

      fetchJobsByStatus: async (
        status: JobStatus[],
        limit?: number,
        lastEvaluatedKey?: string,
      ) => {
        const response = await workerJobActions.getAllJobsByStatusForUserId(
          status,
          undefined, // userId - undefined means current user
          limit,
          lastEvaluatedKey,
        );

        const newJobs = response.jobs?.filter((job) => job !== null) || [];

        if (newJobs.length > 0) {
          get().upsertJobs(newJobs);
        }

        return {
          jobs: newJobs,
          hasMore: response.hasMore || false,
          lastEvaluatedKey: response.lastEvaluatedKey || undefined,
        };
      },

      acceptJob: async (id: string) => {
        set({ isLoading: true });
        const response = await workerJobActions.acceptJob(id);
        if (response) {
          get().upsertJobs([response]);
        }
        set({ isLoading: false });
        return response;
      },

      applyForJob: async (id: string, coverLetter?: string) => {
        set({ isLoading: true });
        const response = await workerJobActions.applyForJob(id, coverLetter);
        if (response) {
          get().upsertJobs([response]);
        }
        set({ isLoading: false });
        return response;
      },

      withdrawFromJob: async (id: string, reason: string) => {
        set({ isLoading: true });
        const response = await workerJobActions.withdrawFromJob(id, reason);
        if (response) {
          get().upsertJobs([response]);
        }
        set({ isLoading: false });
        return response;
      },

      fetchJobById: async (id: string) => {
        const job = await workerJobActions.getJobById(id);
        if (job) {
          get().upsertJobs([job]);
          return job;
        }
        return null;
      },

      // Getters
      getOpenJobs: () =>
        get()
          .workerJobs?.filter(
            (job) =>
              job.itemStatus === JobStatus.Unfilled &&
              dayjs(Number(job.startDate)).isAfter(dayjs()),
          )
          .sort((a, b) =>
            dayjs(Number(a.startDate)).diff(dayjs(Number(b.startDate))),
          ) || [],

      getAppliedJobs: () =>
        get().workerJobs?.filter(
          (job) => job.itemStatus !== JobStatus.Unfilled,
        ) || [],

      /**
       * Upcoming jobs: Job is in the future (hasn't started yet)
       */
      getUpcomingJobs: (): Job[] => {
        const now = dayjs();

        return (
          get()
            .workerJobs?.filter((job) => {
              if (job.itemStatus !== JobStatus.Filled) return false;

              const jobStartDate = dayjs(Number(job.startDate));

              // Upcoming if: job is in the future
              return jobStartDate.isAfter(now);
            })
            .sort((a, b) =>
              dayjs(Number(a.startDate)).diff(dayjs(Number(b.startDate))),
            ) || []
        );
      },

      /**
       * In-progress jobs: Job has started (regardless of daySequence)
       * Worker can still clock in if they missed it
       */
      getInProgressJobs: (): Job[] => {
        const now = dayjs();

        return (
          get()
            .workerJobs?.filter((job) => {
              if (job.itemStatus !== JobStatus.Filled) return false;

              const jobStartDate = dayjs(Number(job.startDate));

              // In progress if: job has started
              return jobStartDate.isSameOrBefore(now);
            })
            .sort((a, b) =>
              dayjs(Number(a.startDate)).diff(dayjs(Number(b.startDate))),
            ) || []
        );
      },

      /**
       * Completed jobs: Job has Completed status
       * (Backend automatically marks job as Completed once daySequence is sorted out)
       */
      getCompletedJobs: (): Job[] => {
        return (
          get()
            .workerJobs?.filter((job) => {
              return job.itemStatus === JobStatus.Completed;
            })
            .sort((a, b) =>
              dayjs(Number(b.startDate)).diff(dayjs(Number(a.startDate))),
            ) || []
        );
      },

      getJobById: (id: string) =>
        get().workerJobs?.find((job) => job.id === id),
    }),
    {
      name: 'worker-job-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        workerJobs: state.workerJobs,
      }),
    },
  ),
);
